class CountUp extends HTMLElement {
  constructor() {
    super();
    this.threshold = +this.getAttribute("threshold") || 0.4;
  }

  connectedCallback() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    this.counters = Array.from(this.querySelectorAll("[data-count]"))
      .map((el) => CountUp.parse(el.querySelector(".count") || el))
      .filter(Boolean);
    if (!this.counters.length) return;
    this.counters.forEach((counter) => {
      counter.el.textContent = counter.format(0);
    });

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.run();
        this.observer.disconnect();
      },
      { threshold: this.threshold },
    );

    this.observer.observe(this);
  }

  static parse(el) {
    const text = el.textContent.trim();
    const match = text.match(/^(\D*?)(\d[\d.,]*)([\s\S]*)$/);
    if (!match) return null;

    const [, prefix, raw, suffix] = match;
    const seps = raw.match(/[.,]/g) || [];
    let thousand = "";
    let decimal = "";
    if (seps.length) {
      const last = seps[seps.length - 1];
      const tail = raw.slice(raw.lastIndexOf(last) + 1);
      if (new Set(seps).size > 1 || tail.length !== 3) {
        decimal = last;
        thousand = seps.find((sep) => sep !== last) || "";
      } else {
        thousand = last;
      }
    }

    const digits = thousand ? raw.split(thousand).join("") : raw;
    const value = parseFloat(decimal ? digits.replace(decimal, ".") : digits);
    const decimals = decimal ? raw.length - raw.lastIndexOf(decimal) - 1 : 0;

    return {
      el,
      text,
      value,
      format: (n) => {
        const [int, dec] = n.toFixed(decimals).split(".");
        const grouped = thousand
          ? int.replace(/\B(?=(\d{3})+(?!\d))/g, thousand)
          : int;
        return prefix + grouped + (dec ? decimal + dec : "") + suffix;
      },
    };
  }

  run() {
    const duration = 1600;
    let start;

    const tick = (t) => {
      start ??= t;
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);

      this.counters.forEach((counter) => {
        counter.el.textContent =
          p < 1 ? counter.format(counter.value * eased) : counter.text;
      });

      if (p < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}
customElements.define("count-up", CountUp);

if (!customElements.get("drag-scroll")) {
  customElements.define(
    "drag-scroll",
    class DragScroll extends HTMLElement {
      constructor() {
        super();
        this.container = this.querySelector("[data-drag-container]");
        this.btnPrev = this.querySelector(".prev");
        this.btnNext = this.querySelector(".next");
        this.scrollThumb = this.querySelector("[data-scroll-thumb]");
        this.items = this.getItems();
        this.currentIndex = 0;

        this.resizeObserver = new ResizeObserver(() => {
          this.initDragToScroll();
        });

        this.resizeObserver.observe(this.container);

        window.addEventListener("resize", () => {
          this.initDragToScroll();
        });
      }

      // Chỉ lấy .item của chính danh sách này, bỏ qua .item của drag-scroll lồng bên trong (vd: variant picker trong thẻ sản phẩm)
      getItems() {
        return [...this.container.querySelectorAll(".item")].filter(
          (item) =>
            item.parentElement.closest("[data-drag-container]") ===
            this.container,
        );
      }

      isVisible(el) {
        return el.offsetParent !== null;
      }

      initDragToScroll() {
        if (!this.isVisible(this.container)) return;

        this.items = this.getItems();

        const isOverflowing = (wrapper) => {
          return wrapper.clientWidth < wrapper.scrollWidth;
        };

        if (isOverflowing(this.container)) {
          this.dragToScroll(this.container);

          if (!this.container.dataset.scrollInit) {
            this.container.dataset.scrollInit = "true";
            this.container.addEventListener("scroll", () =>
              this.handleScroll(),
            );
          }

          if (!this.dataset.btnInit) {
            this.dataset.btnInit = "true";
            this.initButtons();
          }

          this.handleScroll();
        }
      }

      dragToScroll(slider) {
        if (slider.dataset.dragInit === "true") return;
        slider.dataset.dragInit = "true";
        let mouseDown = false;
        let start;
        let scrollLeft;
        let inactiveTimeout;

        // A drag-scroll can nest another drag-scroll (e.g. a variant swatch
        // row inside a carousel card) — bail out here if the mousedown
        // actually belongs to a MORE nested drag-scroll instance (not this
        // one), otherwise dragging the inner one also drags the outer
        // carousel along with it. A plain click (no movement) still reaches
        // the swatch/button/label underneath fine either way, since nothing
        // here calls preventDefault unless a real drag is in progress.
        //
        // stopPropagation on both events also matters when a drag-scroll sits
        // inside a Flickity slideshow (e.g. the variant picker inside a
        // product-cross-sell slide): Flickity's own drag/swipe listener is
        // bound higher up and fires on bubble, so without this it fights the
        // nested drag-scroll for the same gesture and neither scrolls cleanly.
        // Flickity's drag lib (Unidragger) binds its own "pointerdown" listener
        // in addition to "mousedown"/"touchstart" - pointerdown fires as a
        // separate event (not merely an alias), so it must be stopped here too,
        // otherwise Flickity still starts its own drag even though the
        // mousedown/touchstart ones below are stopped.
        slider.addEventListener("pointerdown", (e) => {
          const nestedDragScroll = e.target.closest("drag-scroll");
          if (nestedDragScroll && nestedDragScroll !== this) return;

          e.stopPropagation();
        });

        slider.addEventListener("mousedown", (e) => {
          const nestedDragScroll = e.target.closest("drag-scroll");
          if (nestedDragScroll && nestedDragScroll !== this) return;

          e.stopPropagation();

          mouseDown = true;
          start = e.pageX - slider.offsetLeft;
          scrollLeft = slider.scrollLeft;
        });

        slider.addEventListener(
          "touchstart",
          (e) => {
            const nestedDragScroll = e.target.closest("drag-scroll");
            if (nestedDragScroll && nestedDragScroll !== this) return;

            e.stopPropagation();
          },
          { passive: true },
        );

        slider.addEventListener("mouseup", () => {
          mouseDown = false;

          clearTimeout(inactiveTimeout);
          inactiveTimeout = setTimeout(() => {
            slider.classList.remove("active");
          }, 150);
        });

        slider.addEventListener("mousemove", (e) => {
          if (!mouseDown) return;
          e.preventDefault();

          if (!slider.classList.contains("active")) {
            slider.classList.add("active");
          }

          const x = e.pageX - slider.offsetLeft;
          const walk = (x - start) * 1;
          slider.scrollLeft = scrollLeft - walk;
        });

        slider.addEventListener("mouseleave", () => {
          mouseDown = false;

          clearTimeout(inactiveTimeout);
          inactiveTimeout = setTimeout(() => {
            slider.classList.remove("active");
          }, 150);
        });
      }

      scrollToIndex(index) {
        if (!this.items.length) return;

        const item = this.items[index];
        if (!item) return;

        this.container.scrollTo({
          left: item.offsetLeft,
          behavior: "smooth",
        });

        this.currentIndex = index;
        this.updateButtons();
      }

      itemScrollLeft(item) {
        const containerRect = this.container.getBoundingClientRect();
        const paddingLeft =
          parseFloat(getComputedStyle(this.container).paddingLeft) || 0;
        return (
          item.getBoundingClientRect().left -
          containerRect.left +
          this.container.scrollLeft -
          paddingLeft
        );
      }

      initButtons() {
        if (!this.container) return;
        if (this.btnNext && !this.btnNext.dataset.init) {
          this.btnNext.dataset.init = "true";
          this.btnNext.addEventListener("click", () => {
            const containerLeft = this.container.scrollLeft;
            const nextItem = [...this.items].find((item) => {
              return this.itemScrollLeft(item) > containerLeft + 5;
            });
            if (nextItem) {
              this.container.scrollTo({
                left: this.itemScrollLeft(nextItem),
                behavior: "smooth",
              });
            }
          });
        }

        if (this.btnPrev && !this.btnPrev.dataset.init) {
          this.btnPrev.dataset.init = "true";
          this.btnPrev.addEventListener("click", () => {
            const containerLeft = this.container.scrollLeft;
            const prevItems = [...this.items].filter((item) => {
              return this.itemScrollLeft(item) < containerLeft - 5;
            });
            const prevItem = prevItems[prevItems.length - 1];
            if (prevItem) {
              this.container.scrollTo({
                left: this.itemScrollLeft(prevItem),
                behavior: "smooth",
              });
            }
          });
        }
        this.updateButtons();
      }

      updateButtons() {
        if (!this.btnPrev || !this.btnNext) return;
        const maxScroll =
          this.container.scrollWidth - this.container.clientWidth;
        this.btnPrev.disabled = this.container.scrollLeft <= 5;
        this.btnNext.disabled = this.container.scrollLeft >= maxScroll - 5;
      }

      handleScroll() {
        this.updateButtons();
        this.updateScrollThumb();
      }

      updateScrollThumb() {
        if (!this.scrollThumb) return;
        const { scrollLeft, scrollWidth, clientWidth } = this.container;
        const thumbPercent = Math.min((clientWidth / scrollWidth) * 100, 100);
        const maxScroll = scrollWidth - clientWidth;
        const progress = maxScroll > 0 ? scrollLeft / maxScroll : 0;
        this.scrollThumb.style.width = `${thumbPercent}%`;
        this.scrollThumb.style.left = `${progress * (100 - thumbPercent)}%`;
      }
    },
  );
}

if (!customElements.get("custom-tabs")) {
  customElements.define(
    "custom-tabs",
    class CustomTabs extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.viewAllButton = this.querySelector(".view-all-button");
        this.tab = this.querySelectorAll(".tab-title");
        this.tabContent = this.querySelectorAll(".tab-content");

        for (let i = 0; i < this.tab.length; i++) {
          this.tab[i].addEventListener("click", this.tabActive.bind(this));
        }
      }

      tabActive(event) {
        event.preventDefault();
        event.stopPropagation();
        var $this = event.currentTarget,
          id = $this.getAttribute("href").replace("#", ""),
          idFirstTab = this.tab[0].getAttribute("href").replace("#", ""),
          url = $this.getAttribute("data-url");

        if (!$this.classList.contains("is-open")) {
          this.tab.forEach((element, index) => {
            element.classList.remove("is-open");
            element.setAttribute("aria-selected", "false");
          });

          $this.classList.add("is-open");
          $this.setAttribute("aria-selected", "true");
          if (this.viewAllButton && url) {
            this.viewAllButton.setAttribute("href", url);
          }

          this.tabContent.forEach((element, index) => {
            if (element.getAttribute("id") == id) {
              // Tab ẩn được render trong <template data-tab-lazy> để giảm DOM và không tải ảnh trước,
              // chỉ dựng nội dung khi tab được mở lần đầu
              const lazyTemplate = element.querySelector(
                "template[data-tab-lazy]",
              );
              if (lazyTemplate) {
                lazyTemplate.replaceWith(lazyTemplate.content.cloneNode(true));
              }

              element.classList.add("is-active");

              element.querySelectorAll("drag-scroll").forEach((dragScroll) => {
                dragScroll.initDragToScroll();
              });
            } else {
              element.classList.remove("is-active");
            }
          });
        }
      }
    },
  );
}

if (!customElements.get("variant-selects")) {
  customElements.define(
    "variant-selects",
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        const variantsScript = this.querySelector("[data-product-variants]");

        this.variants = variantsScript
          ? JSON.parse(variantsScript.textContent)
          : [];

        this.updateOptionAvailability();
        this.updateSelectedVariant();
        this.initScrollToSelected();

        // Bấm vào nhãn: trình duyệt mặc định sẽ focus ô radio ẩn và tự cuộn dải tới nó,
        // chồng lên lệnh cuộn mượt bên dưới làm dải bị giật qua lại. Tự chọn radio và focus
        // với preventScroll để chỉ còn một lần cuộn duy nhất.
        this.addEventListener("click", (event) => {
          const label = event.target.closest("label.variant__button-label");
          if (!label || !label.htmlFor) return;

          const input = document.getElementById(label.htmlFor);
          if (!input || !this.contains(input) || input.disabled) return;

          event.preventDefault();
          if (!input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event("input", { bubbles: true }));
            input.dispatchEvent(new Event("change", { bubbles: true }));
          }
          input.focus({ preventScroll: true });
        });

        this.addEventListener("change", (event) => {
          this.updateSelectionMetadata(event);

          this.updateOptionAvailability();
          this.updateSelectedVariant();
          // Khi khách tự chọn: chỉ cuộn nếu giá trị đang bị che, tránh kéo dải đi khi khách đang cuộn xem.
          // Chờ khung hình kế tiếp để bố cục (giá, nhãn...) cập nhật xong rồi mới đo và cuộn.
          cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = requestAnimationFrame(() =>
            this.scrollToSelected(true, { onlyIfHidden: true }),
          );
        });
      }

      disconnectedCallback() {
        this.scrollResizeObserver?.disconnect();
      }

      // Cuộn ngang dải giá trị (size, màu...) tới giá trị đang chọn để khách thấy ngay lựa chọn hiện tại.
      // Thẻ có thể đang ẩn lúc tải trang (tab, popup) nên chờ tới khi có kích thước mới cuộn lần đầu.
      initScrollToSelected() {
        if (this.scrollToSelected(false)) return;
        if (typeof ResizeObserver === "undefined") return;

        this.scrollResizeObserver = new ResizeObserver(() => {
          if (this.scrollToSelected(false)) {
            this.scrollResizeObserver.disconnect();
            this.scrollResizeObserver = null;
          }
        });
        this.scrollResizeObserver.observe(this);
      }

      // Trả về true nếu đã đo được kích thước (đã xử lý xong), false nếu phần tử còn đang ẩn.
      // onlyIfHidden: bỏ qua nếu giá trị đang chọn đã nằm trọn trong vùng nhìn thấy của dải.
      scrollToSelected(smooth, { onlyIfHidden = false } = {}) {
        if (!this.offsetWidth) return false;

        const reduceMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)",
        ).matches;

        this.querySelectorAll(".variant-picker__values").forEach(
          (container) => {
            if (container.scrollWidth <= container.clientWidth) return;

            const item = container
              .querySelector("input:checked")
              ?.closest(".variant-input");
            if (!item) return;

            // Đưa giá trị đang chọn về đầu dải (sát mép trái).
            // Chỉ cuộn ngang trong dải giá trị, không dùng scrollIntoView để tránh trang bị cuộn dọc
            const containerRect = container.getBoundingClientRect();
            const itemRect = item.getBoundingClientRect();

            const isFullyVisible =
              itemRect.left >= containerRect.left - 1 &&
              itemRect.right <= containerRect.right + 1;
            if (onlyIfHidden && isFullyVisible) return;

            const paddingLeft =
              parseFloat(getComputedStyle(container).paddingLeft) || 0;
            const target =
              container.scrollLeft +
              (itemRect.left - containerRect.left) -
              paddingLeft;
            const maxScroll = container.scrollWidth - container.clientWidth;

            container.scrollTo({
              left: Math.max(0, Math.min(target, maxScroll)),
              behavior: smooth && !reduceMotion ? "smooth" : "auto",
            });
          },
        );

        return true;
      }

      updateSelectionMetadata({ target }) {
        const { value, tagName } = target;

        if (tagName === "SELECT" && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.hasAttribute("selected"))
            ?.removeAttribute("selected");

          target.selectedOptions[0].setAttribute("selected", "selected");

          const productFormInput = target.closest(".product-form__input");

          if (productFormInput) {
            const selectedDropdownSwatchValue = productFormInput.querySelector(
              "[data-selected-value] > .swatch",
            );

            if (selectedDropdownSwatchValue) {
              const swatchValue =
                target.selectedOptions[0].dataset.optionSwatchValue;

              if (swatchValue) {
                selectedDropdownSwatchValue.style.setProperty(
                  "--swatch--background",
                  swatchValue,
                );
                selectedDropdownSwatchValue.classList.remove(
                  "swatch--unavailable",
                );
              } else {
                selectedDropdownSwatchValue.style.setProperty(
                  "--swatch--background",
                  "unset",
                );
                selectedDropdownSwatchValue.classList.add(
                  "swatch--unavailable",
                );
              }

              selectedDropdownSwatchValue.style.setProperty(
                "--swatch-focal-point",
                target.selectedOptions[0].dataset.optionSwatchFocalPoint ||
                  "unset",
              );
            }
          }
        } else if (tagName === "INPUT" && target.type === "radio") {
          const selectedSwatchValue = target
            .closest(".product-form__input")
            .querySelector("[data-selected-value]");

          if (selectedSwatchValue) {
            selectedSwatchValue.innerHTML = value;
          }
        }
      }

      updateSelectedVariant() {
        const selectedVariantScript = this.querySelector(
          "[data-selected-variant]",
        );
        if (!selectedVariantScript) return;

        const selectedOptions = this.selectedOptions;

        let selectedVariant = this.variants.find((variant) =>
          variant.options.every(
            (option, index) => option === selectedOptions[index],
          ),
        );

        if (!selectedVariant) {
          selectedVariant = this.variants.find((variant) => {
            return (
              variant.available && variant.options[0] === selectedOptions[0]
            );
          });
        }

        if (!selectedVariant) return;

        selectedVariantScript.textContent = JSON.stringify(selectedVariant);

        this.querySelectorAll("[data-variant-selected-label]").forEach(
          (label) => {
            const index = Number(label.dataset.optionIndex);
            label.textContent = selectedVariant.options[index];
          },
        );

        document.dispatchEvent(
          new CustomEvent("variant-selects:change", {
            detail: {
              event: { target: this },
              target: this,
              selectedVariant,
              selectedOptionValues: this.selectedOptionValues,
            },
          }),
        );
      }

      updateOptionAvailability() {
        const optionGroups = [...this.querySelectorAll("fieldset, select")];

        optionGroups.forEach((group, optionIndex) => {
          const isSelect = group.tagName === "SELECT";

          const inputs = isSelect
            ? [...group.options]
            : [...group.querySelectorAll("input[type='radio']")];

          inputs.forEach((input) => {
            const value = input.value;

            let available = false;

            if (optionIndex === 0) {
              available = this.variants.some(
                (variant) => variant.available && variant.options[0] === value,
              );
            } else {
              const selected = [...this.selectedOptions];
              selected[optionIndex] = value;

              available = this.variants.some((variant) => {
                if (!variant.available) return false;

                return selected.every((option, index) => {
                  if (index > optionIndex) return true;

                  if (!option) return true;

                  return variant.options[index] === option;
                });
              });
            }

            if (isSelect) {
              input.disabled = !available;
            } else {
              input.disabled = !available;

              group
                .querySelector(`label[for="${input.id}"]`)
                ?.classList.toggle("disabled", !available);
            }
          });
        });

        if (this.autoSelectAvailableOption()) {
          this.updateSelectedVariant();
        }
      }

      autoSelectAvailableOption() {
        let changed = false;

        this.querySelectorAll("fieldset").forEach((fieldset) => {
          const checked = fieldset.querySelector("input:checked");

          if (checked && !checked.disabled) return;

          const firstAvailable = fieldset.querySelector("input:not(:disabled)");

          if (!firstAvailable) return;

          changed = true;

          firstAvailable.checked = true;

          const label = fieldset
            .closest(".variant-wrapper")
            ?.querySelector("[data-variant-selected-label]");

          if (label) {
            label.textContent = firstAvailable.value;
          }
        });

        this.querySelectorAll("select").forEach((select) => {
          const current = select.selectedOptions[0];

          if (current && !current.disabled) return;

          const firstAvailable = [...select.options].find(
            (option) => !option.disabled,
          );

          if (!firstAvailable) return;

          changed = true;

          select.value = firstAvailable.value;

          [...select.options].forEach((option) =>
            option.removeAttribute("selected"),
          );

          firstAvailable.selected = true;
          firstAvailable.setAttribute("selected", "selected");

          const wrapper = select.closest(".product-form__input");

          const swatch = wrapper?.querySelector(
            "[data-selected-value] > .swatch",
          );

          if (swatch) {
            const color = firstAvailable.dataset.optionSwatchValue;

            if (color) {
              swatch.style.setProperty("--swatch--background", color);

              swatch.classList.remove("swatch--unavailable");
            } else {
              swatch.style.setProperty("--swatch--background", "unset");

              swatch.classList.add("swatch--unavailable");
            }

            swatch.style.setProperty(
              "--swatch-focal-point",
              firstAvailable.dataset.optionSwatchFocalPoint || "unset",
            );
          }
        });

        return changed;
      }

      getInputForEventTarget(target) {
        return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
      }

      get selectedOptionValues() {
        return [...this.querySelectorAll("fieldset, select")].map((group) => {
          if (group.tagName === "SELECT") {
            return group.selectedOptions[0]?.dataset.optionValueId;
          }

          return group.querySelector("input:checked")?.dataset.optionValueId;
        });
      }

      get selectedOptions() {
        return [...this.querySelectorAll("fieldset, select")].map((group) => {
          if (group.tagName === "SELECT") {
            return group.value;
          }

          return group.querySelector("input:checked")?.value || "";
        });
      }

      get selectedVariant() {
        const script = this.querySelector("[data-selected-variant]");
        return script ? JSON.parse(script.textContent) : null;
      }
    },
  );
}

function getTrafficSource() {
  const params = new URLSearchParams(window.location.search);
  const referrer = document.referrer.toLowerCase();

  const utm_source = (params.get("utm_source") || "").toLowerCase();
  const utm_medium = (params.get("utm_medium") || "").toLowerCase();

  const gclid = params.get("gclid");
  const fbclid = params.get("fbclid");
  const ttclid = params.get("ttclid");

  let channel = "Direct";

  if (
    gclid ||
    (utm_source.includes("google") &&
      ["cpc", "ppc", "paid", "ads"].some((m) => utm_medium.includes(m))) ||
    (utm_source.includes("youtube") &&
      ["cpc", "paid", "ads", "video"].some((m) => utm_medium.includes(m)))
  ) {
    channel = "Google Ads / YouTube Ads";
  } else if (
    ttclid ||
    (utm_source.includes("tiktok") &&
      ["cpc", "paid", "ads", "paid-social"].some((m) => utm_medium.includes(m)))
  ) {
    channel = "TikTok Ads";
  } else if (
    (utm_source.includes("fb") ||
      utm_source.includes("facebook") ||
      utm_source.includes("ig")) &&
    ["cpc", "paid", "ads", "display", "paid-social"].some((m) =>
      utm_medium.includes(m),
    )
  ) {
    channel = "Facebook Ads";
  } else if (referrer.includes("tiktok") || utm_source.includes("tiktok")) {
    channel = "TikTok Organic";
  } else if (referrer.includes("youtube")) {
    channel = "YouTube Organic";
  } else if (
    fbclid ||
    referrer.includes("facebook") ||
    referrer.includes("instagram")
  ) {
    channel = "Facebook Organic";
  } else if (referrer.includes("google")) {
    channel = "Google Organic";
  } else if (utm_source.includes("zalo")) {
    channel = "Zalo";
  }

  return {
    channel,
    utm_source,
    utm_medium,
    landing_page: window.location.href,
    referrer,
  };
}

class Popup extends HTMLElement {
  constructor() {
    super();
    this.initSwipeToClose();
  }
  setOpenPopup(event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.classList.add("active");
    document.body.classList.add("overflow-hidden");
  }
  setClosePopup(event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.classList.remove("active");
    document.body.classList.remove("overflow-hidden");
  }

  initSwipeToClose() {
    const wrapper = this.querySelector(".popup-wrapper");
    if (!wrapper) return;
    Popup.addSwipeToClose(wrapper, (event) => this.setClosePopup(event));
  }

  // Thêm thanh kéo vào đầu `wrapper`, kéo xuống đủ xa / đủ nhanh thì gọi onClose (dùng được cho sheet lồng bên trong popup)
  static addSwipeToClose(wrapper, onClose) {
    const handle = document.createElement("div");
    handle.className = "popup-drag-handle";
    handle.setAttribute("aria-hidden", "true");
    wrapper.prepend(handle);

    const CLOSE_DISTANCE = 100;
    const CLOSE_VELOCITY = 0.5;
    const NO_SWIPE_SELECTOR =
      "input[type='range'], textarea, [data-drag-container], .flickity-viewport";

    const SLIDE_OUT_MS = 280;

    let state = null;

    const overlay = () => wrapper.parentElement?.querySelector(":scope > .overlay");

    const setDragY = (value) => {
      wrapper.style.setProperty("--popup-drag-y", `${value}px`);
      const backdrop = overlay();
      if (backdrop) {
        backdrop.style.opacity = value
          ? String(Math.max(0, 1 - value / Math.max(wrapper.offsetHeight, 1)))
          : "";
      }
    };

    const scrollParent = (target) => {
      for (let el = target; el && el !== wrapper.parentElement; el = el.parentElement) {
        if (
          el.scrollHeight > el.clientHeight + 1 &&
          /(auto|scroll)/.test(getComputedStyle(el).overflowY)
        ) {
          return el;
        }
        if (el === wrapper) break;
      }
      return null;
    };

    const start = (x, y, time, target) => {
      state = {
        x,
        y,
        time,
        deltaY: 0,
        decided: false,
        dragging: false,
        scroller: scrollParent(target),
        blocked: Boolean(target.closest?.(NO_SWIPE_SELECTOR)),
      };
    };

    const move = (x, y, event) => {
      if (!state) return;
      const dx = x - state.x;
      const dy = y - state.y;
      if (!state.decided) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        state.decided = true;
        const atTop = !state.scroller || state.scroller.scrollTop <= 0;
        state.dragging =
          !state.blocked && atTop && dy > 0 && Math.abs(dy) > Math.abs(dx);
        if (!state.dragging) return;
        state.y = y;
        wrapper.classList.add("is-dragging");
        const backdrop = overlay();
        if (backdrop) backdrop.style.transition = "none";
      }
      if (!state.dragging) return;
      if (event.cancelable) event.preventDefault();
      state.deltaY = Math.max(0, y - state.y);
      setDragY(state.deltaY);
    };

    const end = (event) => {
      if (!state) return;
      const { dragging, deltaY, time } = state;
      state = null;
      if (!dragging) return;
      const velocity = deltaY / Math.max(event.timeStamp - time, 1);
      const backdrop = overlay();
      wrapper.classList.remove("is-dragging");
      if (backdrop) backdrop.style.transition = "";

      if (deltaY <= CLOSE_DISTANCE && velocity <= CLOSE_VELOCITY) {
        setDragY(0);
        return;
      }

      wrapper.style.transition = `transform ${SLIDE_OUT_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`;
      if (backdrop) backdrop.style.transition = `opacity ${SLIDE_OUT_MS}ms ease`;
      setDragY(wrapper.offsetHeight + 40);
      if (backdrop) backdrop.style.opacity = "0";

      setTimeout(() => {
        wrapper.style.transition = "none";
        if (backdrop) backdrop.style.transition = "none";
        onClose(event);
        requestAnimationFrame(() => {
          setDragY(0);
          requestAnimationFrame(() => {
            wrapper.style.transition = "";
            if (backdrop) backdrop.style.transition = "";
          });
        });
      }, SLIDE_OUT_MS);
    };

    wrapper.addEventListener(
      "touchstart",
      (event) => {
        if (event.touches.length !== 1) {
          state = null;
          return;
        }
        const touch = event.touches[0];
        start(touch.clientX, touch.clientY, event.timeStamp, event.target);
      },
      { passive: true },
    );
    wrapper.addEventListener(
      "touchmove",
      (event) => {
        const touch = event.touches[0];
        if (touch) move(touch.clientX, touch.clientY, event);
      },
      { passive: false },
    );
    wrapper.addEventListener("touchend", end);
    wrapper.addEventListener("touchcancel", end);

    handle.addEventListener("pointerdown", (event) => {
      if (event.pointerType === "touch") return;
      start(event.clientX, event.clientY, event.timeStamp, handle);
      handle.setPointerCapture(event.pointerId);
    });
    handle.addEventListener("pointermove", (event) => {
      if (event.pointerType === "touch") return;
      move(event.clientX, event.clientY, event);
    });
    handle.addEventListener("pointerup", end);
    handle.addEventListener("pointercancel", end);
    return handle;
  }
}

class RecruitmentSuccessPopup extends Popup {
  constructor() {
    super();
    document.addEventListener("click", (event) => {
      const closeButton = event.target.closest(
        "[data-close-recruitment-popup]",
      );
      if (closeButton) {
        this.setClosePopup(event);
      }
    });
  }
}
customElements.define("recruitment-success-popup", RecruitmentSuccessPopup);

class CollectionFilterPopup extends Popup {
  constructor() {
    super();
    this.mql = window.matchMedia("(max-width: 768px)");
    this.syncMode = () => {
      this.classList.toggle("popup-custom", this.mql.matches);
      if (!this.mql.matches && this.classList.contains("active")) {
        this.setClosePopup();
      }
    };
    this.onDocumentClick = (event) => {
      if (event.target.closest("[data-open-filter-popup]")) {
        this.setOpenPopup(event);
      }
    };
    this.addEventListener("click", (event) => {
      if (event.target.closest("[data-close-filter-popup]")) {
        this.setClosePopup(event);
      }
    });
  }
  connectedCallback() {
    this.syncMode();
    this.mql.addEventListener("change", this.syncMode);
    document.addEventListener("click", this.onDocumentClick);
  }
  disconnectedCallback() {
    this.mql.removeEventListener("change", this.syncMode);
    document.removeEventListener("click", this.onDocumentClick);
  }
}
customElements.define("collection-filter-popup", CollectionFilterPopup);

class PreorderFormPopup extends Popup {
  constructor() {
    super();
    this.webhook = this.getAttribute("data-webhook");
    this.form = this.querySelector("form");
    document.addEventListener("click", (event) => {
      const openButton = event.target.closest("[data-open-preorder-popup]");
      const closeButton = event.target.closest("[data-close-preorder-popup]");
      if (openButton) {
        this.setOpenPopup(event, openButton);
      } else if (closeButton) {
        this.setClosePopup(event);
      }
    });
    this.form.addEventListener("submit", (event) => {
      event.preventDefault();
      this.handleFormSubmit();
    });
  }

  setOpenPopup(event, button) {
    event.preventDefault();
    event.stopPropagation();
    this.classList.add("active");
    document.body.classList.add("overflow-hidden");

    this.productTitle = button.getAttribute("data-product-title");
    this.variantTitle = button.getAttribute("data-variant-title");
  }

  setClosePopup(event) {
    event.preventDefault();
    event.stopPropagation();
    this.classList.remove("active");
    document.body.classList.remove("overflow-hidden");
    if (this.classList.contains("success")) {
      this.classList.remove("success");
      if (this.form) {
        this.form.reset();
      }
    }
  }

  handleFormSubmit() {
    const submitBtn = this.form.querySelector('button[type="submit"]');

    submitBtn.classList.add("is-loading");
    submitBtn.disabled = true;

    let formData = {};
    const inputs = this.querySelectorAll("input");
    inputs.forEach((field) => {
      if (!field.name) return;
      formData[field.name] = field.value;
    });

    formData = {
      ...formData,
      product_title: this.productTitle,
      variant_title: this.variantTitle,
    };

    fetch(this.webhook, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
      mode: "no-cors",
    })
      .then(() => {
        submitBtn.classList.remove("is-loading");
        submitBtn.disabled = false;
        this.classList.add("success");
      })
      .catch((error) => {
        alert("Đã có lỗi xảy ra. Vui lòng thử lại sau.");
        submitBtn.classList.remove("is-loading");
        submitBtn.disabled = false;
      });
  }
}
customElements.define("preorder-form-popup", PreorderFormPopup);

class DiscountPopup extends Popup {
  constructor() {
    super();
    this.webhook = this.getAttribute("data-webhook");
    this.form = this.querySelector("form");
    document.addEventListener("click", (event) => {
      const openButton = event.target.closest("[data-open-discount-popup]");
      const closeButton = event.target.closest("[data-close-discount-popup]");
      if (openButton) {
        this.setOpenPopup(event);
      } else if (closeButton) {
        this.setClosePopup(event);
      }
    });
    if (this.form) {
      this.form.addEventListener("submit", (event) => {
        event.preventDefault();
        this.handleFormSubmit();
      });
    }
    this.maybeAutoShow();
  }

  setClosePopup(event) {
    super.setClosePopup(event);
    if (this.classList.contains("success")) {
      this.classList.remove("success");
      if (this.form) {
        this.form.reset();
      }
    }
  }

  maybeAutoShow() {
    if (this.dataset.autoShow !== "true") return;

    // if (typeof Shopify !== "undefined" && Shopify.designMode) {
    //   this.classList.add("active");
    //   document.body.classList.add("overflow-hidden");
    //   return;
    // }

    const isLighthouse =
      navigator.webdriver || /Lighthouse/i.test(navigator.userAgent);
    if (isLighthouse) return;

    const storageKey = "discountPopupShown";
    const delay = (Number(this.dataset.popupDelay) || 0) * 1000;
    setTimeout(() => {
      if (!sessionStorage.getItem(storageKey)) {
        this.classList.add("active");
        document.body.classList.add("overflow-hidden");
        sessionStorage.setItem(storageKey, "true");
      }
    }, delay);
  }

  handleFormSubmit() {
    const submitBtn = this.form.querySelector('button[type="submit"]');

    submitBtn.classList.add("is-loading");
    submitBtn.disabled = true;

    const formData = {};
    this.querySelectorAll("input").forEach((field) => {
      if (!field.name) return;
      formData[field.name] = field.value;
    });

    const traffic = getTrafficSource();
    formData.traffic_channel = traffic.channel;
    formData.url = traffic.landing_page;

    fetch(this.webhook, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
      mode: "no-cors",
    })
      .then(() => {
        submitBtn.classList.remove("is-loading");
        submitBtn.disabled = false;
        this.classList.add("success");
      })
      .catch((error) => {
        alert("Đã có lỗi xảy ra. Vui lòng thử lại sau.");
        submitBtn.classList.remove("is-loading");
        submitBtn.disabled = false;
      });
  }
}
customElements.define("discount-popup", DiscountPopup);

if (!customElements.get("cart-discount-inline")) {
  customElements.define(
    "cart-discount-inline",
    class CartDiscountInline extends HTMLElement {
      constructor() {
        super();
        this.text = {
          apply: this.dataset.applyText,
          applying: this.dataset.loadingText,
          empty: this.dataset.emptyCodeText,
          error: this.dataset.errorText,
          invalid: this.dataset.invalidText,
          success: this.dataset.successText,
        };

        this.input = this.querySelector("[data-discount-input]");
        this.applyBtn = this.querySelector("[data-discount-apply]");
        this.message = this.querySelector("[data-discount-message]");

        if (!this.input || !this.applyBtn) return;

        this.applyBtn.addEventListener("click", (event) => {
          event.preventDefault();
          const code = this.input.value.trim();
          if (!code) {
            this.showMessage(this.text.empty, "error");
            return;
          }
          this.applyDiscount(code);
        });

        this.input.addEventListener("keydown", (event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            this.applyBtn.click();
          }
        });
      }


      async applyDiscount(code) {
        try {
          const appliedCodes = getAppliedDiscountCodes();
          if (!appliedCodes.includes(code)) {
            appliedCodes.push(code);
          }

          this.applyBtn.disabled = true;
          this.applyBtn.textContent = this.text.applying;

          const dataString = await theme.cart.updateDiscount(
            appliedCodes.join(","),
          );

          if (!dataString) {
            this.showMessage(this.text.error, "error");
            return;
          }
          const data = JSON.parse(dataString);

          if (
            data.discount_codes.find((discount) => {
              return discount.code === code && discount.applicable === false;
            })
          ) {
            this.showMessage(this.text.invalid, "error");
          } else {
            this.input.value = "";
            this.showMessage(this.text.success, "success");
            document.dispatchEvent(new CustomEvent("cart:build"));
          }
        } catch (error) {
          console.error(error);
          this.showMessage(this.text.error, "error");
        } finally {
          this.applyBtn.disabled = false;
          this.applyBtn.textContent = this.text.apply;
        }
      }

      showMessage(message, type = "success") {
        if (!this.message) return;
        this.message.textContent = message;
        this.message.classList.remove("success", "error");
        this.message.classList.add(type);
        clearTimeout(this.messageTimeout);
        this.messageTimeout = setTimeout(() => {
          this.message.textContent = "";
          this.message.classList.remove("success", "error");
        }, 3000);
      }
    },
  );
}

function getAppliedDiscountCodes() {
  const scope = document.getElementById("CartPageForm") || document;
  return [
    ...scope.querySelectorAll("[data-discounts] .cart__discount-remove"),
  ]
    .map((item) => item.dataset.code)
    .filter(Boolean);
}

function syncVoucherCards() {
  const appliedCodes = getAppliedDiscountCodes();
  document.querySelectorAll("[data-voucher-apply]").forEach((card) => {
    const isApplied = appliedCodes.includes(card.dataset.code);
    card.classList.toggle("is-applied", isApplied);
    card.classList.remove("is-loading", "is-invalid");
    card.disabled = isApplied;
  });
}

document.querySelectorAll("[data-discounts]").forEach((el) => {
  new MutationObserver(syncVoucherCards).observe(el, {
    childList: true,
    subtree: true,
  });
});

document.addEventListener("click", async (event) => {
  const card = event.target.closest("[data-voucher-apply]");
  if (!card || card.disabled) return;

  const code = card.dataset.code;
  if (!code) return;

  let applied = false;

  try {
    card.disabled = true;
    card.classList.add("is-loading");

    const appliedCodes = getAppliedDiscountCodes();
    if (!appliedCodes.includes(code)) {
      appliedCodes.push(code);
    }

    const dataString = await theme.cart.updateDiscount(appliedCodes.join(","));

    if (!dataString) {
      throw new Error(card.dataset.errorText);
    }

    const data = JSON.parse(dataString);
    const invalid = data.discount_codes.find((discount) => {
      return discount.code === code && discount.applicable === false;
    });

    if (invalid) {
      card.classList.add("is-invalid");
      setTimeout(() => card.classList.remove("is-invalid"), 2000);
    } else {
      applied = true;
      card.classList.add("is-applied");
      document.dispatchEvent(new CustomEvent("cart:build"));
    }
  } catch (error) {
    console.error(error);
    card.classList.add("is-invalid");
    setTimeout(() => card.classList.remove("is-invalid"), 2000);
  } finally {
    card.disabled = applied;
    card.classList.remove("is-loading");
  }
});

if (!customElements.get("voucher-popup")) {
  customElements.define(
    "voucher-popup",
    class VoucherPopup extends Popup {
      constructor() {
        super();

        this.text = {
          apply: this.dataset.applyText,
          applying: this.dataset.loadingText,
          empty: this.dataset.emptyCodeText,
          error: this.dataset.errorText,
          invalid: this.dataset.invalidText,
          success: this.dataset.successText,
        };

        document.addEventListener("click", (event) => {
          const openButton = event.target.closest("[data-open-voucher-popup]");
          const closeButton = event.target.closest(
            "[data-close-voucher-popup]",
          );
          if (openButton) {
            this.setOpenPopup(event);
          } else if (closeButton) {
            this.setClosePopup(event);
          }
        });

        this.cards = this.querySelectorAll(".voucher-card");
        this.applyBtn = this.querySelector(".voucher-popup-apply-btn");
        this.input = this.querySelector(".voucher-popup-input");
        this.message = this.querySelector(".voucher-popup-message");

        this.popupWrapper = this.querySelector(".popup-wrapper");

        this.popupWrapper?.addEventListener("click", (event) => {
          if (event.target.closest("[data-close-voucher-popup]")) return;

          event.stopPropagation();
        });

        if (this.applyBtn) {
          this.applyBtn.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            const code = this.input.value.trim();
            if (!code) {
              this.showMessage(this.text.empty, "error");
              return;
            }
            this.applyDiscount(code);
          });
        }

        this.cards.forEach((card) => {
          card.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            const code = card.dataset.code;
            if (!code) {
              this.showMessage(this.text.empty, "error");
              return;
            }
            this.applyDiscount(code);
          });
        });
      }

      async applyDiscount(code) {
        try {
          const appliedCodes = getAppliedDiscountCodes();
          if (!appliedCodes.includes(code)) {
            appliedCodes.push(code);
          }

          this.input.value = code;
          this.applyBtn.textContent = this.text.applying;

          const dataString = await theme.cart.updateDiscount(
            appliedCodes.join(","),
          );

          if (!dataString) {
            this.showMessage(this.text.error, "error");
            return;
          }
          const data = JSON.parse(dataString);

          if (
            data.discount_codes.find((discount) => {
              return discount.code === code && discount.applicable === false;
            })
          ) {
            this.input.value = "";
            this.showMessage(this.text.invalid, "error");
          } else {
            this.input.value = "";
            this.showMessage(this.text.success, "success");
            document.dispatchEvent(new CustomEvent("cart:build"));
          }
          this.applyBtn.textContent = this.text.apply;
        } catch (error) {
          console.error(error);
          this.showMessage(this.text.error, "error");
          this.applyBtn.textContent = this.text.apply;
        }
      }

      showMessage(message, type = "success") {
        if (!this.message) return;
        this.message.textContent = message;
        this.message.classList.remove("success", "error");
        this.message.classList.add(type);
        clearTimeout(this.messageTimeout);
        this.messageTimeout = setTimeout(() => {
          this.message.textContent = "";
          this.message.classList.remove("success", "error");
        }, 3000);
      }
    },
  );
}

if (!customElements.get("vat-invoice-popup")) {
  customElements.define(
    "vat-invoice-popup",
    class VatInvoicePopup extends Popup {
      constructor() {
        super();

        this.errorText = this.dataset.errorText;
        this.savedPrefix = this.dataset.savedPrefix;
        this.defaultSubtitleText = this.dataset.defaultSubtitleText;

        this.form = this.querySelector("[data-vat-invoice-form]");
        this.typeRadios = this.querySelectorAll("[data-vat-type-radio]");
        this.fieldGroups = this.querySelectorAll("[data-vat-fields]");
        this.saveBtn = this.querySelector("[data-vat-invoice-save]");
        this.cancelBtn = this.querySelector("[data-vat-invoice-cancel]");

        this.popupWrapper = this.querySelector(".popup-wrapper");
        this.popupWrapper?.addEventListener("click", (event) => {
          if (event.target.closest("[data-close-vat-invoice-popup]")) return;
          event.stopPropagation();
        });

        this.addEventListener("click", (event) => {
          if (event.target.closest("[data-close-vat-invoice-popup]")) {
            this.setClosePopup(event);
          }
        });

        this.typeRadios.forEach((radio) => {
          radio.addEventListener("change", () => this.syncFieldGroups());
        });
        this.syncFieldGroups();

        if (this.form) {
          this.form.addEventListener("submit", (event) => {
            event.preventDefault();
            this.handleSave();
          });
        }

        this.cancelBtn?.addEventListener("click", (event) => {
          event.preventDefault();
          this.handleRemove();
        });
      }

      connectedCallback() {
        if (this.parentDrawer) return;
        this.parentDrawer = this.closest("#CartDrawer");
        if (!this.parentDrawer) return;

        // theme.Drawers.close() doesn't know this popup exists, so it can
        // close the drawer while this stays "active", leaving body.overflow-hidden
        // stuck. Force-close this popup whenever its parent drawer closes.
        new MutationObserver(() => {
          if (
            !this.parentDrawer.classList.contains("drawer--is-open") &&
            this.classList.contains("active")
          ) {
            this.setClosePopup({
              preventDefault() {},
              stopPropagation() {},
            });
          }
        }).observe(this.parentDrawer, {
          attributes: true,
          attributeFilter: ["class"],
        });

        document.body.append(this);
      }

      syncFieldGroups() {
        const checkedRadio = this.querySelector(
          "[data-vat-type-radio]:checked",
        );
        const activeType = checkedRadio ? checkedRadio.value : "business";

        this.fieldGroups.forEach((group) => {
          const isActive = group.dataset.vatFields === activeType;
          group.hidden = !isActive;
          group.querySelectorAll("input").forEach((input) => {
            input.disabled = !isActive;
            input.required = isActive && input.dataset.vatOptional !== "true";
          });
        });
      }

      async handleSave() {
        if (!this.form.reportValidity()) return;

        this.saveBtn.disabled = true;
        this.saveBtn.classList.add("is-loading");

        try {
          const formData = new FormData(this.form);
          const newAttributes = {};
          formData.forEach((value, key) => {
            newAttributes[key] = value;
          });
          newAttributes.vat_invoice_requested = "true";

          const cartResponse = await fetch("/cart.js", {
            headers: { Accept: "application/json" },
          });
          const currentCart = await cartResponse.json();
          const mergedAttributes = {
            ...currentCart.attributes,
            ...newAttributes,
          };

          await theme.cart._updateCart({
            url: "/cart/update.js",
            data: JSON.stringify({ attributes: mergedAttributes }),
          });

          document.dispatchEvent(new CustomEvent("cart:build"));
          this.updateTriggerLabel(newAttributes);
          if (this.cancelBtn) this.cancelBtn.hidden = false;
          this.setClosePopup({
            preventDefault() {},
            stopPropagation() {},
          });
        } catch (error) {
          console.error(error);
          alert(this.errorText);
        } finally {
          this.saveBtn.disabled = false;
          this.saveBtn.classList.remove("is-loading");
        }
      }

      async handleRemove() {
        this.cancelBtn.disabled = true;
        this.cancelBtn.classList.add("is-loading");

        try {
          const cartResponse = await fetch("/cart.js", {
            headers: { Accept: "application/json" },
          });
          const currentCart = await cartResponse.json();
          const clearedAttributes = { ...currentCart.attributes };
          [
            "vat_invoice_requested",
            "vat_invoice_type",
            "vat_company_name",
            "vat_tax_code",
            "vat_email",
            "vat_address",
            "vat_full_name",
          ].forEach((key) => {
            clearedAttributes[key] = "";
          });

          await theme.cart._updateCart({
            url: "/cart/update.js",
            data: JSON.stringify({ attributes: clearedAttributes }),
          });

          document.dispatchEvent(new CustomEvent("cart:build"));

          this.form
            .querySelectorAll('input[type="text"], input[type="email"]')
            .forEach((input) => {
              input.value = "";
            });
          const businessRadio = this.querySelector(
            '[data-vat-type-radio][value="business"]',
          );
          if (businessRadio) businessRadio.checked = true;
          this.syncFieldGroups();

          this.resetTriggerLabel();
          this.cancelBtn.hidden = true;
          this.setClosePopup({
            preventDefault() {},
            stopPropagation() {},
          });
        } catch (error) {
          console.error(error);
          alert(this.errorText);
        } finally {
          this.cancelBtn.disabled = false;
          this.cancelBtn.classList.remove("is-loading");
        }
      }

      resetTriggerLabel() {
        document
          .querySelectorAll("[data-vat-invoice-subtitle]")
          .forEach((el) => {
            el.textContent = this.defaultSubtitleText;
          });
      }

      updateTriggerLabel(attributes) {
        const label =
          attributes.vat_invoice_type === "individual"
            ? attributes.vat_full_name
            : attributes.vat_company_name;
        const text = label ? this.savedPrefix + label : this.savedPrefix;
        document
          .querySelectorAll("[data-vat-invoice-subtitle]")
          .forEach((el) => {
            el.textContent = text;
          });
      }
    },
  );
}

document.addEventListener("click", (event) => {
  const openButton = event.target.closest("[data-open-vat-invoice-popup]");
  if (!openButton) return;

  const unique = openButton.closest("#CartDrawer") ? "drawer" : "page";
  document
    .querySelector(`vat-invoice-popup[data-unique="${unique}"]`)
    ?.setOpenPopup(event);
});

class ArticleToc extends HTMLElement {
  connectedCallback() {
    this.headingSelector =
      this.dataset.tabOfContents || "h1, h2, h3, h4, h5, h6";
    this.content = document.querySelector(".js-toc-content");
    this.tocBox = this.querySelector(".nav-toc");
    this.fixedToc = document.querySelector(".fixed-toc");

    if (!this.content || !this.tocBox) return;

    this.initHeadingIds();
    this.initToc();
    this.initFixedToc();
    this.initToggle();
    this.checkEmptyToc();
  }

  toSlug(text) {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  }

  initHeadingIds() {
    const headings = this.content.querySelectorAll("h1, h2, h3, h4, h5, h6");
    headings.forEach((heading) => {
      if (!heading.id) {
        heading.id = this.toSlug(heading.textContent);
      }
    });
  }

  initToc() {
    tocbot.init({
      headingsOffset: 70,
      scrollSmoothOffset: -70,
      tocSelector: ".blog__table .nav-toc",
      contentSelector: ".js-toc-content",
      headingSelector: this.headingSelector,
      hasInnerContainers: true,
    });
  }

  initFixedToc() {
    if (!this.fixedToc || !this.tocBox.childNodes.length) return;

    const clone = this.tocBox.cloneNode(true);

    this.fixedToc.querySelector(".fixed-toc-content")?.appendChild(clone);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.fixedToc.classList.remove("open");
            this.fixedToc.classList.add("hidden");
            const fixedContent =
              this.fixedToc.querySelector(".fixed-toc-content");
            if (fixedContent) fixedContent.inert = true;
            this.fixedToc
              .querySelector(".toc-toggle-btn")
              ?.setAttribute("aria-expanded", "false");
          } else {
            this.fixedToc.classList.remove("hidden");
          }
        });
      },
      { threshold: 0 },
    );

    observer.observe(this);
  }

  initToggle() {
    this.querySelectorAll(".toc-toggle").forEach((button) => {
      button.addEventListener("click", () => {
        const isOpen = button.classList.toggle("active");
        button.setAttribute("aria-expanded", isOpen);
        const content = button.nextElementSibling;
        if (content) content.inert = !isOpen;
      });
    });

    if (!this.fixedToc) return;
    const fixedContent = this.fixedToc.querySelector(".fixed-toc-content");
    const fixedButtons = this.fixedToc.querySelectorAll(".toc-toggle-btn");
    const setFixedOpen = (isOpen) => {
      this.fixedToc.classList.toggle("open", isOpen);
      if (fixedContent) fixedContent.inert = !isOpen;
      fixedButtons[0]?.setAttribute("aria-expanded", isOpen);
    };
    fixedButtons.forEach((button) => {
      button.addEventListener("click", () => {
        setFixedOpen(!this.fixedToc.classList.contains("open"));
      });
    });
    this.fixedToc.addEventListener("click", (event) => {
      if (event.target.closest(".toc-link")) setFixedOpen(false);
    });
    this.fixedToc.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.fixedToc.classList.contains("open")) {
        setFixedOpen(false);
        fixedButtons[0]?.focus();
      }
    });
  }

  checkEmptyToc() {
    if (this.tocBox.childNodes.length) return;

    this.classList.add("hidden");
    this.fixedToc?.classList.add("hidden");
  }

  disconnectedCallback() {
    if (window.tocbot) {
      tocbot.destroy();
    }
  }
}

customElements.define("article-toc", ArticleToc);

document.addEventListener("DOMContentLoaded", () => {
  const videos = document.querySelectorAll(".lazy-video");

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const video = entry.target;

        if (!entry.isIntersecting) {
          if (!video.paused) video.pause();
          return;
        }

        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          video.play().catch(() => {});
          return;
        }

        if (video.dataset.lazyLoadStarted) return;
        video.dataset.lazyLoadStarted = "true";
        video.load();

        video.addEventListener(
          "loadeddata",
          () => {
            video.play().catch(() => {});
          },
          { once: true },
        );
      });
    },
    {
      rootMargin: "200px",
      threshold: 0,
    },
  );

  videos.forEach((video) => observer.observe(video));
});

// <tilt-card>: hiệu ứng nghiêng 3D khi rê chuột.
// Cách dùng:
//   <tilt-card max="10" scale="1.03">
//     <a data-tilt-target>...</a>   (phần tử sẽ nghiêng; không có thì nghiêng chính tilt-card)
//   </tilt-card>
// Thuộc tính: max = độ nghiêng tối đa (mặc định 10), scale = độ phóng khi hover (mặc định 1.03),
// perspective = độ sâu 3D tính bằng px (mặc định 800).
// Không chạy trên màn hình cảm ứng hoặc khi người dùng bật giảm chuyển động.
const tiltCardMediaQuery = window.matchMedia(
  "(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)",
);

class TiltCard extends HTMLElement {
  constructor() {
    super();
    this.frame = 0;
    this.rect = null;
    this.pointerX = 0;
    this.pointerY = 0;

    this.onEnter = this.onEnter.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onLeave = this.onLeave.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.render = this.render.bind(this);
  }

  connectedCallback() {
    if (!tiltCardMediaQuery.matches) return;
    this.target = this.querySelector("[data-tilt-target]") || this;

    this.addEventListener("pointerenter", this.onEnter);
    this.addEventListener("pointermove", this.onMove, { passive: true });
    this.addEventListener("pointerleave", this.onLeave);
  }

  disconnectedCallback() {
    this.removeEventListener("pointerenter", this.onEnter);
    this.removeEventListener("pointermove", this.onMove);
    this.removeEventListener("pointerleave", this.onLeave);
    this.onLeave();
  }

  get max() {
    return parseFloat(this.getAttribute("max")) || 10;
  }

  get scale() {
    return parseFloat(this.getAttribute("scale")) || 1.03;
  }

  get perspective() {
    return parseFloat(this.getAttribute("perspective")) || 800;
  }

  onEnter(event) {
    if (event.pointerType !== "mouse") return;
    this.rect = this.getBoundingClientRect();
    // Vị trí phần tử đổi khi cuộn trang, đo lại ở khung hình kế tiếp
    window.addEventListener("scroll", this.onScroll, { passive: true });
  }

  onMove(event) {
    if (event.pointerType !== "mouse") return;
    this.pointerX = event.clientX;
    this.pointerY = event.clientY;
    if (!this.frame) this.frame = requestAnimationFrame(this.render);
  }

  onLeave() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.rect = null;
    window.removeEventListener("scroll", this.onScroll);
    if (this.target) this.target.style.transform = "";
  }

  onScroll() {
    this.rect = null;
  }

  render() {
    this.frame = 0;
    if (!this.rect) this.rect = this.getBoundingClientRect();

    const { rect, max, scale } = this;
    const rotateY = ((this.pointerX - rect.left) / rect.width - 0.5) * max * 2;
    const rotateX = (0.5 - (this.pointerY - rect.top) / rect.height) * max * 2;
    this.target.style.transform = `perspective(${this.perspective}px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(${scale}, ${scale}, ${scale})`;
  }
}

if (!customElements.get("tilt-card"))
  customElements.define("tilt-card", TiltCard);

// <product-card-02>: thẻ sản phẩm có chọn biến thể (snippets/product-card-02.liquid).
// Đổi biến thể -> cập nhật giá (theo % giảm theo thời gian, snippet ru9-discount), link "Xem chi tiết", trạng thái nút thêm giỏ.
class ProductCard02 extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.addButton = this.querySelector("[data-card-add]");
    this.addButton?.addEventListener("click", () => this.addToCart());
  }

  get variantId() {
    const variant = this.querySelector("variant-selects")?.selectedVariant;
    return Number(variant ? variant.id : this.dataset.variantId);
  }

  get comparePricing() {
    return this.hasAttribute("data-compare-pricing");
  }

  compareDiscount(price, compare) {
    return compare > price ? Math.round(((compare - price) / compare) * 100) : 0;
  }

  get selection() {
    const variant = this.querySelector("variant-selects")?.selectedVariant;
    const id = Number(variant ? variant.id : this.dataset.variantId);
    const price = Number(variant ? variant.price : this.dataset.price) || 0;
    const compare = Number(variant ? variant.compare_at_price : this.dataset.compare) || 0;
    const discount = this.comparePricing ? 0 : this.getDiscount(id);
    return {
      id,
      available: variant ? variant.available : this.dataset.available === "true",
      price: Math.round(price - (price * discount) / 100),
      compare: Math.max(compare, price),
    };
  }

  getDiscount(variantId) {
    const script = this.querySelector("[data-variant-discounts]");
    if (!script) return Number(this.dataset.discount) || 0;
    try {
      return Number(JSON.parse(script.textContent)[variantId]) || 0;
    } catch (error) {
      return 0;
    }
  }

  formatMoney(cents) {
    return window.theme?.Currency
      ? theme.Currency.formatMoney(cents, theme.settings.moneyFormat)
      : (cents / 100).toLocaleString();
  }

  updateVariant(variant) {
    this.dataset.variantId = variant.id;
    let discount;
    let finalPrice;
    let regularPrice;
    if (this.comparePricing) {
      regularPrice = Math.max(Number(variant.compare_at_price) || 0, variant.price);
      discount = this.compareDiscount(variant.price, regularPrice);
      finalPrice = variant.price;
    } else {
      discount = this.getDiscount(variant.id);
      regularPrice = variant.price;
      finalPrice = variant.price - (variant.price * discount) / 100;
    }

    const finalEl = this.querySelector("[data-card-price]");
    if (finalEl) finalEl.innerHTML = this.formatMoney(finalPrice);

    const originalEl = this.querySelector("[data-card-price-original]");
    if (originalEl) {
      originalEl.innerHTML =
        discount > 0 ? this.formatMoney(regularPrice) : "";
      originalEl.hidden = discount <= 0;
    }

    const badge = this.querySelector("[data-card-badge]");
    if (badge) {
      badge.textContent = `-${Math.round(discount)}%`;
      badge.hidden = discount <= 0;
    }

    const viewLink = this.querySelector("[data-card-view]");
    if (viewLink)
      viewLink.href = `${viewLink.href.split("?")[0]}?variant=${variant.id}`;

    if (this.addButton) {
      this.addButton.disabled = !variant.available;
      const label = this.addButton.querySelector("span");
      if (label) {
        label.textContent = variant.available
          ? this.addButton.dataset.labelAdd
          : this.addButton.dataset.labelSoldOut;
      }
    }
  }

  addToCart() {
    if (!window.theme?.cart || !this.variantId) return;
    const button = this.addButton;
    button.disabled = true;
    button.classList.add("is-loading");

    theme.cart
      .addItems([{ id: this.variantId, quantity: 1 }])
      .then(() => {
        document.dispatchEvent(new CustomEvent("cart:build"));
        document.dispatchEvent(new CustomEvent("cart:open"));
      })
      .catch((error) => console.error(error))
      .finally(() => {
        button.disabled = false;
        button.classList.remove("is-loading");
      });
  }
}

if (!customElements.get("product-card-02")) {
  customElements.define("product-card-02", ProductCard02);
  // variant-selects phát sự kiện ở document: một listener chung cho mọi thẻ
  document.addEventListener("variant-selects:change", (event) => {
    const { target, selectedVariant } = event.detail || {};
    if (!selectedVariant || !target) return;
    target.closest("product-card-02")?.updateVariant(selectedVariant);
  });
}

function renderLazyDrawer(drawer) {
  if (!drawer) return [];
  return [...drawer.querySelectorAll("template[data-lazy-drawer]")].map((template) => {
    const fragment = template.content.cloneNode(true);
    const roots = [...fragment.children];
    template.replaceWith(fragment);
    return roots;
  }).flat();
}

function openLazyNavDrawer() {
  const roots = renderLazyDrawer(document.getElementById("NavDrawer"));
  if (!roots.length) return;
  theme.headerNavMobile?.init();
  roots.forEach((root) => theme.collapsibles?.init(root));
}

function openLazyCartDrawer() {
  renderLazyDrawer(document.getElementById("CartDrawer"));
}

document.addEventListener(
  "click",
  (event) => {
    if (event.target.closest(".js-drawer-open-nav")) openLazyNavDrawer();
    if (event.target.closest(".js-drawer-open-cart")) openLazyCartDrawer();
  },
  true,
);
document.addEventListener("drawerOpen.NavDrawer", openLazyNavDrawer);
document.addEventListener("drawerOpen.CartDrawer", openLazyCartDrawer);
